import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft, User, Lock, Eye, EyeOff, Phone, Mail } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { registerRider } from '../../services/authService';
import { useCountry } from '../../contexts/CountryContext';

const EnhancedRiderAuth = ({ onBack, onAuthSuccess }) => {
  const ctx = useCountry();
  const [formData, setFormData] = useState({
    phone: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.username || !formData.password || !formData.phone) {
      toast({ title: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    const nationalPhone = formData.phone.replace(/^0+/, '');
    if (nationalPhone.length !== 9) {
      toast({ title: 'Invalid phone number', description: 'Enter your 10-digit number starting with 0 (e.g. 0991 234 567)', variant: 'destructive' });
      return;
    }
    
    if (formData.username.length < 3) {
      toast({ title: 'Username must be at least 3 characters', variant: 'destructive' });
      return;
    }
    
    if (formData.password.length < 6) {
      toast({ title: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    
    const userData = {
      phone: formData.phone.replace(/^0+/, ''),
      country_code: ctx?.callingCode && ctx.callingCode !== '+' ? ctx.callingCode : '+265',
      country: ctx?.detectedIso3 || 'MWI',
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email || null,
      username: formData.username,
      password: formData.password
    };

    const result = await registerRider(userData);
    setLoading(false);
    
    if (result.success) {
      toast({ 
        title: 'Registration Successful!', 
        description: 'You can now log in with your credentials'
      });
      onAuthSuccess(result.user);
    } else {
      toast({
        title: 'Registration Failed',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl dark:bg-gray-800">
        <div className="p-8">
          <button onClick={onBack} className="mb-6 flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Rider Registration</h2>
            <p className="text-gray-600 dark:text-gray-400">Create your account to start booking rides</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phone Number */}
            <div>
              <Label htmlFor="phone" className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <div className="flex mt-2">
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-l-lg border border-r-0 border-gray-300 dark:border-gray-600 flex items-center">
                  <span className="text-gray-700 dark:text-gray-300 font-medium" data-testid="rider-calling-code">{ctx?.callingCode && ctx.callingCode !== '+' ? ctx.callingCode : '+265'}</span>
                </div>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="0991 234 567"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  className="rounded-l-none"
                  required
                />
              </div>
            </div>

            {/* First Name */}
            <div>
              <Label htmlFor="firstName" className="text-gray-700 dark:text-gray-300">First Name *</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                className="mt-2"
                required
              />
            </div>

            {/* Middle Name */}
            <div>
              <Label htmlFor="middleName" className="text-gray-700 dark:text-gray-300">Middle Name (Optional)</Label>
              <Input
                id="middleName"
                type="text"
                placeholder="Enter middle name"
                value={formData.middleName}
                onChange={(e) => setFormData({...formData, middleName: e.target.value})}
                className="mt-2"
              />
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="lastName" className="text-gray-700 dark:text-gray-300">Last Name *</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                className="mt-2"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address (Optional)
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-2"
              />
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="username" className="text-gray-700 dark:text-gray-300">Username *</Label>
              <Input
                id="username"
                type="text"
                placeholder="Choose a username"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                className="mt-2"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Lowercase letters, numbers and underscores only</p>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password *
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">At least 6 characters</p>
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-gray-700 dark:text-gray-300">Confirm Password *</Label>
              <div className="relative mt-2">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold mt-6"
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Complete Registration'}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default EnhancedRiderAuth;
